import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createLocalizedUrl } from '../utils/languageUtils';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from './LanguageSelector';
import CompareSpanishCities from './CompareSpanishCities';

const Footer = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  return (
    <footer className="border-t border-gray-200 pt-8 pb-6">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 w-full">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="inline-block hover:opacity-90 transition">
              <img 
                src="/assets/LiveLoveSpain.svg"
                alt="LiveLoveSpain Logo"
                className="h-10 w-auto"
                loading="eager"
                decoding="sync"
              />
            </Link>
            <p className="mt-4 text-gray-600 max-w-md">
              {t('footer.company.description')}
            </p>
            
            {/* Language Selector */}
            <div className="mt-4">
              <LanguageSelector />
            </div>
            
            {/* Social Media Links */}
            <div className="mt-4 flex space-x-4">
              <a 
                href="https://www.facebook.com/people/Livelovespain/61572933601843/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-blue-600 transition-colors"
                aria-label="Follow us on Facebook"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.77,7.46H14.5v-1.9c0-.9.6-1.1,1-1.1h3V.5h-4.33C10.24.5,9.5,3.44,9.5,5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4Z"/>
                </svg>
              </a>
              <a 
                href="https://www.instagram.com/live.lovespain/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-pink-600 transition-colors"
                aria-label="Follow us on Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12,2.2c3.2,0,3.6,0,4.9,0.1c3.3,0.1,4.8,1.7,4.9,4.9c0.1,1.3,0.1,1.6,0.1,4.8c0,3.2,0,3.6-0.1,4.8c-0.1,3.2-1.7,4.8-4.9,4.9c-1.3,0.1-1.6,0.1-4.9,0.1c-3.2,0-3.6,0-4.8-0.1c-3.3-0.1-4.8-1.7-4.9-4.9c-0.1-1.3-0.1-1.6-0.1-4.8c0-3.2,0-3.6,0.1-4.8c0.1-3.2,1.7,4.8,4.9,4.9C8.4,2.2,8.8,2.2,12,2.2z M12,0C8.7,0,8.3,0,7.1,0.1c-4.4,0.2-6.8,2.6-7,7C0,8.3,0,8.7,0,12s0,3.7,0.1,4.9c0.2,4.4,2.6,6.8,7,7C8.3,24,8.7,24,12,24s3.7,0,4.9-0.1c4.4-0.2,6.8-2.6,7-7C24,15.7,24,15.3,24,12s0-3.7-0.1-4.9c-0.2-4.4-2.6-6.8-7-7C15.7,0,15.3,0,12,0z M12,5.8c-3.4,0-6.2,2.8-6.2,6.2s2.8,6.2,6.2,6.2s6.2-2.8,6.2-6.2S15.4,5.8,12,5.8z M12,16c-2.2,0-4-1.8-4-4s1.8-4,4-4s4,1.8,4,4S14.2,16,12,16z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <div className="text-gray-900 font-semibold mb-4">
              {t('footer.sections.product.title')}
            </div>
            <ul className="space-y-3">
              <li>
                <a 
                  href={createLocalizedUrl("/#features", currentLanguage)} 
                  className="text-gray-600 hover:text-blue-600 transition"
                >
                  {t('header.features')}
                </a>
              </li>
              <li>
                <a 
                  href={createLocalizedUrl("/#pricing", currentLanguage)} 
                  className="text-gray-600 hover:text-blue-600 transition"
                >
                  {t('header.pricing')}
                </a>
              </li>
              <li>
                <Link 
                  to={createLocalizedUrl("/cost-explorer", currentLanguage)} 
                  className="text-gray-600 hover:text-blue-600 transition"
                >
                  {t('welcome.costExplorer')}
                </Link>
              </li>
              <li>
                <Link 
                  to={createLocalizedUrl("/guides", currentLanguage)} 
                  className="text-gray-600 hover:text-blue-600 transition"
                >
                  {t('welcome.guides')}
                </Link>
              </li>
              <li>
                <Link 
                  to={createLocalizedUrl("/tools", currentLanguage)} 
                  className="text-gray-600 hover:text-blue-600 transition"
                >
                  {t('welcome.tools')}
                </Link>
              </li>
              <li>
                <Link 
                  to={createLocalizedUrl("/support", currentLanguage)} 
                  className="text-gray-600 hover:text-blue-600 transition"
                >
                  {t('common.helpCenter')}
                </Link>
              </li>
              <li>
                <a 
                  href="https://crm.livelovespain.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition"
                >
                  {t('common.agencySolutions')}
                </a>
              </li>
              <li>
                <a 
                  href="https://livelovespain.endorsely.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-600 transition"
                >
                  {t('common.affiliateProgram')}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <div className="text-gray-900 font-semibold mb-4">
              {t('footer.sections.legal.title')}
            </div>
            <ul className="space-y-3">
              <li>
                <Link to={createLocalizedUrl("/privacy", currentLanguage)} className="text-gray-600 hover:text-blue-600 transition">
                  {t('footer.sections.legal.links.privacy')}
                </Link>
              </li>
              <li>
                <Link to={createLocalizedUrl("/terms", currentLanguage)} className="text-gray-600 hover:text-blue-600 transition">
                  {t('footer.sections.legal.links.terms')}
                </Link>
              </li>
              <li>
                <Link to={createLocalizedUrl("/cookies", currentLanguage)} className="text-gray-600 hover:text-blue-600 transition">
                  {t('footer.sections.legal.links.cookies')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Compare Cities Section */}
        <CompareSpanishCities />

        {/* Bottom Bar */}
        <div className="mt-8 pt-6 border-t border-gray-200 w-full">
          <div className="flex justify-center">
            <p className="text-gray-600 text-sm">
              {t('footer.company.rights', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
